import { Injectable } from "@angular/core";
import { UsersService, AccessRights } from "./users.service";
import { UserObjectGroupAcl } from "app/models/UserObjectGroupAcl";

interface ObjectGroupUserLinkLike {
  userId?: number;
  UserId?: number;
  acl?: number;
  ACL?: number;
}

interface ObjectGroupLike {
  Id?: number;
  id?: number;
  Users?: ObjectGroupUserLinkLike[];
  users?: ObjectGroupUserLinkLike[];
  objects?: number[];
  ObjectIds?: number[];
  objectIds?: number[];
  Name?: string;
  IsChecked?: boolean;
}

export interface GroupAclMaps {
  objectAclById: Map<number, number>;
  groupAclById: Map<number, number>;
}

export interface DeviceListEditAccess {
  canEditObjects: boolean;
  editObjectIds: Set<number> | null;
}

export interface DeviceListCommandAccess {
  canSendCommands: boolean;
  commandObjectIds: Set<number> | null;
}

@Injectable({ providedIn: "root" })
export class AccessRightsService {
  constructor(private usersService: UsersService) {}

  public isAdminUser(): boolean {
    const user = this.usersService.getCurrentUser();
    return !!user && this.hasAdminRights(user);
  }

  public buildAclMaps(groups: ObjectGroupLike[] | null | undefined): GroupAclMaps {
    const objectAclById = new Map<number, number>();
    const groupAclById = new Map<number, number>();
    const userId = this.getCurrentUserId();
    if (!userId || !groups) return { objectAclById, groupAclById };

    for (const group of groups) {
      const groupId = group.Id ?? 0;
      const link = this.findUserLink(group, userId);
      const userAcl = this.getLinkAcl(link);
      if (groupId) groupAclById.set(groupId, userAcl);

      for (const objectId of this.getGroupObjectIds(group)) {
        const existing = objectAclById.get(objectId) ?? 0;
        objectAclById.set(objectId, existing | userAcl);
      }
    }

    return { objectAclById, groupAclById };
  }

  public canEditGroup(groupId: number, maps: GroupAclMaps): boolean {
    return this.canAccessGroup(groupId, maps, UserObjectGroupAcl.EditGroup);
  }

  public canDeleteGroup(groupId: number, maps: GroupAclMaps): boolean {
    return this.canAccessGroup(groupId, maps, UserObjectGroupAcl.DeleteGroup);
  }

  public canEditObject(objectId: number, maps: GroupAclMaps): boolean {
    return this.canAccessObject(objectId, maps, UserObjectGroupAcl.EditObjects);
  }

  public canDeleteObject(objectId: number, maps: GroupAclMaps): boolean {
    return this.canAccessObject(objectId, maps, UserObjectGroupAcl.DeleteObjects);
  }

  public canChangeImei(objectId: number, maps: GroupAclMaps): boolean {
    return this.canAccessObject(objectId, maps, UserObjectGroupAcl.ChangeImei);
  }

  public getDeviceListEditAccess(groups: ObjectGroupLike[] | null | undefined): DeviceListEditAccess {
    const editObjectIds = this.collectAccessibleObjectIds(groups, UserObjectGroupAcl.EditObjects);
    return {
      canEditObjects: editObjectIds === null || editObjectIds.size > 0,
      editObjectIds
    };
  }

  public getDeviceListCommandAccess(groups: ObjectGroupLike[] | null | undefined): DeviceListCommandAccess {
    const commandObjectIds = this.collectAccessibleObjectIds(groups, UserObjectGroupAcl.SendCommands);
    return {
      canSendCommands: commandObjectIds === null || commandObjectIds.size > 0,
      commandObjectIds
    };
  }

  private getCurrentUserId(): number | undefined {
    return this.usersService.getCurrentUser()?.Id;
  }

  private hasAdminRights(user: { CanDo(accessRight: AccessRights): boolean }): boolean {
    return user.CanDo(AccessRights.EDITUSERS) || user.CanDo(AccessRights.EDIT_ALL_USERS);
  }

  private getGroupUsers(group: ObjectGroupLike): ObjectGroupUserLinkLike[] {
    return group.Users ?? group.users ?? [];
  }

  private getGroupObjectIds(group: ObjectGroupLike): number[] {
    return group.objects ?? group.ObjectIds ?? group.objectIds ?? [];
  }

  private findUserLink(group: ObjectGroupLike, userId: number): ObjectGroupUserLinkLike | undefined {
    return this.getGroupUsers(group).find(link => link.userId === userId || link.UserId === userId);
  }

  private getLinkAcl(link: ObjectGroupUserLinkLike | undefined): number {
    return link?.acl ?? link?.ACL ?? 0;
  }

  private hasAclPermission(acl: number, permission: UserObjectGroupAcl): boolean {
    return (acl & permission) !== 0;
  }

  private canAccessGroup(groupId: number, maps: GroupAclMaps, permission: UserObjectGroupAcl): boolean {
    if (this.isAdminUser()) return true;
    return this.hasAclPermission(maps.groupAclById.get(groupId) ?? 0, permission);
  }

  private canAccessObject(objectId: number, maps: GroupAclMaps, permission: UserObjectGroupAcl): boolean {
    if (this.isAdminUser()) return true;
    return this.hasAclPermission(maps.objectAclById.get(objectId) ?? 0, permission);
  }

  private collectAccessibleObjectIds(
    groups: ObjectGroupLike[] | null | undefined,
    permission: UserObjectGroupAcl
  ): Set<number> | null {
    if (this.isAdminUser()) return null;

    const userId = this.getCurrentUserId();
    const objectIds = new Set<number>();
    if (!userId || !groups) return objectIds;

    for (const group of groups) {
      const acl = this.getLinkAcl(this.findUserLink(group, userId));
      if (!this.hasAclPermission(acl, permission)) continue;

      for (const objectId of this.getGroupObjectIds(group)) {
        objectIds.add(objectId);
      }
    }

    return objectIds;
  }
}
